import {
  Component,
  EventEmitter,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  inject,
  ChangeDetectorRef
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { Subject, filter, first, takeUntil } from 'rxjs';
import { ActionButton, ActionButtonActionType, ActionButtonType, FormDialog } from '@shared/models';
import { FormDialogComponent } from '@shared/components/form-dialog/form-dialog.component';
import { Column } from '@shared/components/simple-table';
import { MessageDialogComponent } from '@shared/components/message-dialog/message-dialog.component';
import { ZButtonComponent } from '../z-button/z-button.component';
import { SimpleTableComponent } from '../simple-table/simple-table.component';
import { EmptyDataComponent } from '../empty-data/empty-data.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-widget-data-list',
    templateUrl: './widget-data-list.component.html',
    styleUrls: ['./widget-data-list.component.scss'],
    imports: [ZButtonComponent, SimpleTableComponent, EmptyDataComponent, TranslatePipe]
})
export class WidgetDataListComponent implements OnDestroy, OnInit {
  private cdr = inject(ChangeDetectorRef);

  readonly columns = input.required<Column[]>();
  readonly allowSelection = input<boolean>(false);
  readonly formControlRef =
    input.required<FormControl<any[]>>();
  readonly formDialog = input.required<FormDialog>();
  readonly actions = input<ActionButton[]>([
    {
      name: 'Visualizar',
      icon: 'visibility',
      action: ActionButtonActionType.VIEW,
      color: 'green',
      type: ActionButtonType.BUTTON,
    },
    {
      name: 'Editar',
      icon: 'edit_note',
      action: ActionButtonActionType.EDIT,
      color: 'green',
      type: ActionButtonType.BUTTON,
    },
    {
      name: 'Deletar',
      icon: 'delete_outline',
      action: ActionButtonActionType.DELETE,
      color: 'green',
      type: ActionButtonType.BUTTON,
    },
  ]);

  readonly customModalRef = input<any>();
  readonly customModalMaxWidth = input<string>('640px');

  @Output()
  tableActions: EventEmitter<ActionButton> = new EventEmitter();

  @Output()
  dialogOpened: EventEmitter<void> = new EventEmitter();

  @Output()
  dialogClosed: EventEmitter<void> = new EventEmitter();

  private destroySubject: Subject<boolean> = new Subject();

  constructor(private matDialog: MatDialog) { }

  ngOnInit(): void { }

  ngOnDestroy(): void {
    this.destroySubject.next(true);
    this.destroySubject.complete();
  }

  tableAction(action: ActionButton) {
    switch (action.action) {
      case ActionButtonActionType.EDIT:
        this.openEditDialog(action);
        break;
      case ActionButtonActionType.DELETE:
        this.openDeleteDialog(action);
        break;
      case ActionButtonActionType.VIEW:
        this.openEditDialog(action, true);
        break;
      default:
        this.tableActions.emit(action);
        break;
    }
  }

  openDialog(isEditMode: boolean): MatDialogRef<FormDialogComponent, any> {
    const templateRef = this.customModalRef()
      ? this.customModalRef()
      : FormDialogComponent;
    return this.matDialog.open(templateRef, {
      width: '100%',
      maxWidth: this.customModalMaxWidth(),
      maxHeight: '90vh',
      panelClass: 'responsive-dialog',
      data: {
        ...this.formDialog(),
        isEditMode: isEditMode,
      },
    });
  }

  openNewItemDialog() {
    const dialogRef = this.openDialog(false);

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroySubject))
      .subscribe((value) => {
        if (value) {
          const currentList = this.formControlRef().value || [];
          const newList = [...currentList, value];

          this.formControlRef().setValue(newList);
          this.cdr.detectChanges();
          this.cdr.markForCheck();
        }

        this.dialogClosed.emit();
      });
  }

  openEditDialog(action: ActionButton, isViewMode: boolean = false) {
    const dialogRef = this.openDialog(true);

    dialogRef
      .afterOpened()
      .pipe(first())
      .subscribe(() => {
        if (isViewMode) {
          this.formDialog().formGroup?.disable({ emitEvent: false });
        }
        this.formDialog().formGroup?.patchValue(action.row);
        this.dialogOpened.emit();
      });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroySubject))
      .subscribe((value) => {
        if (value) {
          const currentList = this.formControlRef().value || [];

          const newList = currentList.map((item, index) => {
            return index === action.index ? value : item;
          });

          this.formControlRef().setValue(newList);
          this.cdr.detectChanges();
          this.cdr.markForCheck();
        }

        this.formDialog().formGroup?.enable({ emitEvent: false });
        this.formDialog().formGroup?.reset();
        this.dialogClosed.emit();
      });
  }

  openDeleteDialog(action: ActionButton) {
    const dialogRef = this.matDialog.open(MessageDialogComponent, {
      minWidth: 500,
      data: {
        title: 'Confirmação',
        message: 'Deseja realmente remover esse item ?',
        icon: 'warning',
        iconColor: 'red',
        buttons: [
          {
            label: 'Cancelar',
            value: false,
          },
          {
            label: 'Confirmar',
            class: 'bg-primary-brand-color',
            color: 'primary',
            value: true,
          },
        ],
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((value) => !!value),
        takeUntil(this.destroySubject)
      )
      .subscribe(() => {
        const currentList = this.formControlRef().value || [];

        const newList = currentList.filter(
          (item, index) => index !== action.index
        );

        this.formControlRef().setValue(newList);
        this.cdr.detectChanges();
        this.cdr.markForCheck();
      });
  }
}
